"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, type ReactNode } from "react";
import { isDebugCaptureEnabled } from "@/lib/debug-capture/env";
import { hasResumableSessionDraft } from "@/lib/debug-capture/session-draft";
import { DebugCaptureFloatingButton } from "./DebugCaptureFloatingButton";

// ponytail: jszip/modern-screenshot stay in ./index. Do not import that module
// (or @/lib/debug-capture) from this file — webpack would put them on every page.
const DebugCaptureRuntime = dynamic(
  () =>
    import("./index").then((mod) => ({ default: mod.DebugCaptureProvider })),
  {
    ssr: false,
    loading: () => (
      <DebugCaptureFloatingButton isArmed={false} onToggle={() => undefined} />
    ),
  },
);

export function DebugCaptureGate({ children }: { children: ReactNode }) {
  const enabled = isDebugCaptureEnabled();
  const [runtime, setRuntime] = useState(false);
  const [autoArm, setAutoArm] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (hasResumableSessionDraft()) {
      setRuntime(true);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || runtime) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (!event.altKey || !event.ctrlKey || event.key.toLowerCase() !== "b") {
        return;
      }
      event.preventDefault();
      setAutoArm(true);
      setRuntime(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled, runtime]);

  if (!enabled) {
    return children;
  }

  const launch = () => {
    setAutoArm(true);
    setRuntime(true);
  };

  return (
    <>
      {children}
      {runtime ? (
        <DebugCaptureRuntime autoArm={autoArm} />
      ) : (
        <DebugCaptureFloatingButton isArmed={false} onToggle={launch} />
      )}
    </>
  );
}
